import { useMemo, useState } from "react";
import { brand } from "../../../config/theme";
import type { GroupedMenuOptions, SidebarMenuItem, SidebarThemeClasses } from "./types";
import { filterItems, groupItems, resolveMenuFavorites } from "./menuGroups";
import { useMenuFavorites } from "./useMenuFavorites";

interface Props {
  items: SidebarMenuItem[];
  options: GroupedMenuOptions;
  isDarkMode: boolean;
  themeClasses: SidebarThemeClasses;
  isActiveRoute: (itemId: string, path?: string) => boolean;
  onNavigate: (path: string) => void;
  onCloseMobileMenu: () => void;
}

// Variante del menú lateral para apps con muchas opciones parecidas (Reportes:
// doce). Lo que la justifica es encontrar rápido, no decorar: buscador arriba,
// los fijados primero y el resto por tema, cada uno con una línea de qué trae.
export function GroupedNav({
  items,
  options,
  isDarkMode,
  themeClasses,
  isActiveRoute,
  onNavigate,
  onCloseMobileMenu,
}: Props) {
  const [query, setQuery] = useState("");
  const { favorites, toggle } = useMenuFavorites(options.scope);
  // Qué estrella se acaba de marcar, para animar sólo esa.
  const [popped, setPopped] = useState<{ id: string; n: number } | null>(null);

  const filtered = useMemo(() => filterItems(items, query), [items, query]);
  const groups = useMemo(() => groupItems(filtered), [filtered]);
  const favItems = useMemo(() => resolveMenuFavorites(favorites, items), [favorites, items]);
  const searching = query.trim() !== "";

  const onStar = (id: string) => {
    const adding = !favorites.includes(id);
    toggle(id);
    setPopped(adding ? { id, n: (popped?.n ?? 0) + 1 } : null);
  };

  const renderItem = (item: SidebarMenuItem, keyPrefix: string) => {
    const path = item.path || item.route || item.id;
    const active = isActiveRoute(item.id, path);
    const fav = favorites.includes(item.id);
    const pop = popped?.id === item.id ? popped.n : 0;
    return (
      <div key={`${keyPrefix}-${item.id}`} className="relative group">
        <button
          type="button"
          disabled={item.disabled}
          aria-current={active ? "page" : undefined}
          onClick={() => {
            if (item.disabled) return;
            onNavigate(path);
            onCloseMobileMenu();
          }}
          className={`w-full flex items-start gap-3 pl-3 pr-9 py-2 rounded-xl text-left transition-colors ${
            item.disabled
              ? "opacity-50 cursor-not-allowed"
              : active
                ? "shadow-sm text-gray-900"
                : `${themeClasses.buttonText} ${themeClasses.buttonTextHover} ${themeClasses.buttonHover}`
          }`}
          style={active && !item.disabled ? { backgroundColor: brand.primary } : undefined}
        >
          <i className={`${item.icon} text-lg leading-6 flex-shrink-0`} />
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-medium leading-6 truncate">{item.label}</span>
            {item.description && (
              <span
                className={`block text-xs leading-4 mt-0.5 ${
                  active ? "text-gray-800" : isDarkMode ? "text-gray-500" : "text-gray-500"
                }`}
              >
                {item.description}
              </span>
            )}
          </span>
        </button>
        {/* Botón hermano, no anidado: un <button> dentro de otro es HTML
            inválido y el clic de la estrella también navegaría. */}
        <button
          type="button"
          aria-pressed={fav}
          aria-label={`${fav ? options.removeFavoriteLabel : options.addFavoriteLabel}: ${item.label}`}
          title={fav ? options.removeFavoriteLabel : options.addFavoriteLabel}
          onClick={() => onStar(item.id)}
          className={`absolute right-1.5 top-2 w-7 h-7 flex items-center justify-center rounded-md transition-opacity focus-visible:opacity-100 ${
            fav
              ? `opacity-100 ${active ? "text-gray-900" : "text-amber-400"}`
              : `opacity-0 group-hover:opacity-100 ${active ? "text-gray-800" : "text-gray-400 hover:text-amber-400"}`
          }`}
        >
          {pop > 0 && fav && (
            <span
              key={`burst-${pop}`}
              aria-hidden
              className="absolute inset-1 rounded-full bg-amber-400/60 motion-safe:animate-starBurst opacity-0"
            />
          )}
          <i
            key={`star-${pop}`}
            className={`${fav ? "ri-star-fill" : "ri-star-line"} relative text-base leading-none ${
              pop > 0 ? "motion-safe:animate-starPop" : ""
            }`}
          />
        </button>
      </div>
    );
  };

  const heading = (text: string) => (
    <p
      className={`px-3 pt-4 pb-1 text-[11px] font-semibold uppercase tracking-wider ${
        isDarkMode ? "text-gray-500" : "text-gray-400"
      }`}
    >
      {text}
    </p>
  );

  return (
    <div>
      <div className="relative mb-1">
        <i
          className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary}`}
          aria-hidden
        />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Escape" && setQuery("")}
          placeholder={options.searchPlaceholder}
          aria-label={options.searchPlaceholder}
          className={`w-full pl-9 pr-9 py-2 rounded-xl text-sm border outline-none transition-colors focus:border-primary-500 [&::-webkit-search-cancel-button]:appearance-none ${
            isDarkMode
              ? "bg-[#1f1f1f] border-[#4A4A4A] text-white placeholder-gray-500"
              : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
          }`}
        />
        {searching && (
          <button
            type="button"
            onClick={() => setQuery("")}
            aria-label={options.clearSearchLabel}
            title={options.clearSearchLabel}
            className={`absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-md ${themeClasses.buttonText} ${themeClasses.buttonTextHover}`}
          >
            <i className="ri-close-line" aria-hidden />
          </button>
        )}
      </div>

      {!searching && (
        <section>
          {heading(options.favoritesLabel)}
          {favItems.length > 0 ? (
            <div className="space-y-0.5">{favItems.map((it) => renderItem(it, "fav"))}</div>
          ) : (
            <p className={`px-3 pb-1 text-xs leading-5 ${themeClasses.textSecondary}`}>
              <i className="ri-star-line text-amber-400 mr-1" aria-hidden />
              {options.favoritesHint}
            </p>
          )}
        </section>
      )}

      {groups.map((g) => (
        <section key={g.group}>
          {g.group && heading(g.group)}
          <div className="space-y-0.5">{g.items.map((it) => renderItem(it, "grp"))}</div>
        </section>
      ))}

      {searching && filtered.length === 0 && (
        <p className={`px-3 py-6 text-sm text-center ${themeClasses.textSecondary}`}>{options.emptyText}</p>
      )}
    </div>
  );
}
